import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import {
  AlertTriangle,
  Plus,
  Upload,
  RefreshCw,
  Filter,
  BarChart3,
  Link2,
  FileSpreadsheet,
  Settings,
} from "lucide-react";

const STATUS_COLORS = {
  New: "bg-avida-blue-100 text-avida-blue-800",
  Notified: "bg-amber-100 text-amber-800",
  Corrected: "bg-purple-100 text-purple-800",
  Verified: "bg-emerald-100 text-emerald-800",
  Closed: "bg-slate-100 text-slate-800",
};

const TYPE_LABELS = {
  ClockInOut: "Clock In/Out",
  GPS: "GPS",
  Verification: "Verification",
  ServiceCode: "Service Code",
  Duration: "Duration",
  Other: "Other",
};

const SOURCE_LABELS = {
  Manual: { label: "Manual", color: "bg-slate-100 text-slate-700" },
  Import: { label: "Import", color: "bg-blue-100 text-blue-700" },
  SandataImport: { label: "Sandata", color: "bg-amber-100 text-amber-700" },
};

const NOTIFICATION_METHODS = ["Email", "Phone", "InPerson", "Other"];

const EVVInboxPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [exceptions, setExceptions] = useState([]);
  const [agingReport, setAgingReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [visits, setVisits] = useState([]);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [needsLinkingFilter, setNeedsLinkingFilter] = useState("all");
  const [showAgingReport, setShowAgingReport] = useState(false);
  
  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    exception_type: "",
    description: "",
    caregiver_id: "",
    client_id: "",
    exception_date: "",
    reason: "",
    visit_id: "",
  });
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  
  // Link Visit modal state (Sprint 9)
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkException, setLinkException] = useState(null);
  const [linkVisitId, setLinkVisitId] = useState("");
  const [linkVisitsSearch, setLinkVisitsSearch] = useState([]);
  const [linkLoading, setLinkLoading] = useState(false);
  
  const canManage = ["Admin", "ComplianceManager", "Scheduler"].includes(user?.role);
  const canImport = ["Admin", "ComplianceManager"].includes(user?.role);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status_filter", statusFilter);
      if (typeFilter !== "all") params.append("exception_type", typeFilter);
      if (sourceFilter !== "all") params.append("source", sourceFilter);
      if (needsLinkingFilter === "yes") params.append("needs_linking", "true");
      if (needsLinkingFilter === "no") params.append("needs_linking", "false");
      
      // Core data - all users with EVV_EXCEPTION_READ can access
      const [excRes, agingRes] = await Promise.all([
        api.get(`/evv-exceptions?${params.toString()}`),
        api.get("/evv-exceptions/aging-report"),
      ]);
      
      setExceptions(excRes.data.exceptions || []);
      setAgingReport(agingRes.data);
      
      // Additional data for users who can manage exceptions (Admin, ComplianceManager, Scheduler)
      if (canManage) {
        try {
          const [clientsRes, usersRes] = await Promise.all([
            api.get("/clients?page_size=100"),
            api.get("/users?page_size=100"),
          ]);
          setClients(clientsRes.data.clients || []);
          setCaregivers((usersRes.data.users || []).filter(u => u.role === "Caregiver" && !u.is_archived));
        } catch (err) {
          // Silently fail - user may not have permission to list clients/users
          console.log("Could not load clients/users for create form");
        }
      }
    } catch (error) {
      toast.error("Failed to load EVV exceptions");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, sourceFilter, needsLinkingFilter, canManage]);

  const fetchVisits = async (clientId) => {
    if (!clientId) {
      setVisits([]);
      return;
    }
    try {
      const res = await api.get(`/visits?client_id=${clientId}&page_size=50`);
      setVisits(res.data.visits || []);
    } catch (error) {
      console.error("Failed to fetch visits", error);
    }
  };

  // Search visits for Link Visit modal
  const searchVisitsForLink = async (exceptionId, clientId) => {
    if (!clientId) return;
    setLinkLoading(true);
    try {
      const res = await api.get(`/visits?client_id=${clientId}&page_size=20`);
      setLinkVisitsSearch(res.data.visits || []);
    } catch (error) {
      toast.error("Failed to search visits");
    } finally {
      setLinkLoading(false);
    }
  };

  // Handle Link Visit action (Sprint 9)
  const handleLinkVisit = async () => {
    if (!linkException || !linkVisitId) {
      toast.error("Please select a visit to link");
      return;
    }
    setLinkLoading(true);
    try {
      await api.post(`/evv-exceptions/${linkException.id}/link-visit`, {
        visit_id: linkVisitId
      });
      toast.success("Visit linked successfully");
      setShowLinkModal(false);
      setLinkException(null);
      setLinkVisitId("");
      setLinkVisitsSearch([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to link visit");
    } finally {
      setLinkLoading(false);
    }
  };

  // Open link modal for exception
  const openLinkModal = (exc, e) => {
    e.stopPropagation();
    setLinkException(exc);
    setLinkVisitId("");
    setShowLinkModal(true);
    if (exc.client_id) {
      searchVisitsForLink(exc.id, exc.client_id);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...createForm };
      if (!payload.visit_id) delete payload.visit_id;
      
      await api.post("/evv-exceptions", payload);
      toast.success("EVV exception created");
      setShowCreateModal(false);
      setCreateForm({
        exception_type: "",
        description: "",
        caregiver_id: "",
        client_id: "",
        exception_date: "",
        reason: "",
        visit_id: "",
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create exception");
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    if (!importFile) return;
    
    const formData = new FormData();
    formData.append("file", importFile);
    
    try {
      const res = await api.post("/evv-exceptions/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(res.data);
      toast.success(`Imported ${res.data.imported} of ${res.data.total_rows} exceptions`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <Layout title="EVV Exceptions">
      <div className="space-y-6">
        {/* Aging Summary Cards */}
        {agingReport && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="bg-avida-blue-50 border-avida-blue-200">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-avida-blue-700">{agingReport.total_open}</div>
                <div className="text-sm text-avida-blue-600">Open Exceptions</div>
              </CardContent>
            </Card>
            {agingReport.aging_buckets?.slice(0, 4).map((bucket, idx) => (
              <Card key={idx} className={bucket.count > 0 && idx >= 2 ? "bg-red-50 border-red-200" : "bg-slate-50"}>
                <CardContent className="p-4">
                  <div className={`text-2xl font-bold ${bucket.count > 0 && idx >= 2 ? "text-red-700" : "text-slate-700"}`}>
                    {bucket.count}
                  </div>
                  <div className="text-sm text-slate-600">{bucket.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Filters and Actions */}
        <div className="bg-white rounded-lg border border-slate-200 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-500" />
              <Label className="text-sm text-slate-600">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-32" data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="New">New</SelectItem>
                  <SelectItem value="Notified">Notified</SelectItem>
                  <SelectItem value="Corrected">Corrected</SelectItem>
                  <SelectItem value="Verified">Verified</SelectItem>
                  <SelectItem value="Closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-sm text-slate-600">Type</Label>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-36" data-testid="type-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="ClockInOut">Clock In/Out</SelectItem>
                  <SelectItem value="GPS">GPS</SelectItem>
                  <SelectItem value="Verification">Verification</SelectItem>
                  <SelectItem value="ServiceCode">Service Code</SelectItem>
                  <SelectItem value="Duration">Duration</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Source Filter (Sprint 9) */}
            <div className="flex items-center gap-2">
              <Label className="text-sm text-slate-600">Source</Label>
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger className="w-32" data-testid="source-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  <SelectItem value="MANUAL">Manual</SelectItem>
                  <SelectItem value="IMPORT">Import</SelectItem>
                  <SelectItem value="SANDATA_IMPORT">Sandata</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Needs Linking Filter (Sprint 9) */}
            <div className="flex items-center gap-2">
              <Label className="text-sm text-slate-600">Linking</Label>
              <Select value={needsLinkingFilter} onValueChange={setNeedsLinkingFilter}>
                <SelectTrigger className="w-36" data-testid="needs-linking-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="yes">Needs Linking</SelectItem>
                  <SelectItem value="no">Linked</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="h-4 w-4 mr-1" /> Refresh
            </Button>

            <Button variant="outline" size="sm" onClick={() => setShowAgingReport(!showAgingReport)}>
              <BarChart3 className="h-4 w-4 mr-1" /> {showAgingReport ? "Hide" : "Show"} Report
            </Button>

            <div className="ml-auto flex gap-2 flex-wrap">
              {/* Sandata Import Wizard (Sprint 9) - Admin/Compliance only */}
              {canImport && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate("/sandata-import")}
                    data-testid="sandata-import-btn"
                  >
                    <FileSpreadsheet className="h-4 w-4 mr-1" /> Sandata Import
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate("/sandata-mappings")}
                    data-testid="sandata-mappings-btn"
                  >
                    <Settings className="h-4 w-4 mr-1" /> ID Mappings
                  </Button>
                </>
              )}
              
              {canManage && (
                <>
                  <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="import-btn">
                        <Upload className="h-4 w-4 mr-1" /> Import CSV
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Import EVV Exceptions from CSV</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleImport} className="space-y-4">
                        <div className="text-sm text-slate-600">
                          <p className="mb-2">Expected columns:</p>
                          <code className="text-xs bg-slate-100 p-2 block rounded">
                            exception_type, description, caregiver_email, client_medicaid_id, exception_date, reason, visit_id (optional)
                          </code>
                        </div>
                        <Input
                          type="file"
                          accept=".csv"
                          onChange={(e) => setImportFile(e.target.files[0])}
                          data-testid="import-file-input"
                        />
                        {importResult && (
                          <div className="text-sm">
                            <p className="text-green-600">Imported: {importResult.imported}/{importResult.total_rows}</p>
                            {importResult.errors?.length > 0 && (
                              <div className="text-red-600 mt-2 max-h-32 overflow-y-auto">
                                {importResult.errors.map((err, i) => (
                                  <p key={i}>Row {err.row}: {err.error}</p>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        <Button type="submit" disabled={!importFile} data-testid="import-submit-btn">
                          Import
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>

                  <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
                    <DialogTrigger asChild>
                      <Button size="sm" data-testid="create-exception-btn">
                        <Plus className="h-4 w-4 mr-1" /> New Exception
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                      <DialogHeader>
                        <DialogTitle>Create EVV Exception</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleCreate} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Exception Type *</Label>
                            <Select
                              value={createForm.exception_type}
                              onValueChange={(v) => setCreateForm({ ...createForm, exception_type: v })}
                            >
                              <SelectTrigger data-testid="create-type-select">
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(TYPE_LABELS).map(([k, v]) => (
                                  <SelectItem key={k} value={k}>{v}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Exception Date *</Label>
                            <Input
                              type="date"
                              value={createForm.exception_date}
                              onChange={(e) => setCreateForm({ ...createForm, exception_date: e.target.value })}
                              required
                              data-testid="create-date-input"
                            />
                          </div>
                        </div>

                        <div>
                          <Label>Client *</Label>
                          <Select
                            value={createForm.client_id}
                            onValueChange={(v) => {
                              setCreateForm({ ...createForm, client_id: v, visit_id: "" });
                              fetchVisits(v);
                            }}
                          >
                            <SelectTrigger data-testid="create-client-select">
                              <SelectValue placeholder="Select client" />
                            </SelectTrigger>
                            <SelectContent>
                              {clients.filter(c => !c.archived_at).map((c) => (
                                <SelectItem key={c.id} value={c.id}>
                                  {c.last_name}, {c.first_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label>Caregiver *</Label>
                          <Select
                            value={createForm.caregiver_id}
                            onValueChange={(v) => setCreateForm({ ...createForm, caregiver_id: v })}
                          >
                            <SelectTrigger data-testid="create-caregiver-select">
                              <SelectValue placeholder="Select caregiver" />
                            </SelectTrigger>
                            <SelectContent>
                              {caregivers.map((cg) => (
                                <SelectItem key={cg.id} value={cg.id}>
                                  {cg.last_name}, {cg.first_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {visits.length > 0 && (
                          <div>
                            <Label>Link to Visit (Optional)</Label>
                            <Select
                              value={createForm.visit_id}
                              onValueChange={(v) => setCreateForm({ ...createForm, visit_id: v === "none" ? "" : v })}
                            >
                              <SelectTrigger data-testid="create-visit-select">
                                <SelectValue placeholder="Select visit (optional)" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">None</SelectItem>
                                {visits.map((v) => (
                                  <SelectItem key={v.id} value={v.id}>
                                    {formatDate(v.scheduled_start)} - {v.service_type}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        <div>
                          <Label>Description * (min 10 chars)</Label>
                          <Textarea
                            value={createForm.description}
                            onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                            placeholder="Describe the EVV exception..."
                            required
                            minLength={10}
                            data-testid="create-description-input"
                          />
                        </div>

                        <div>
                          <Label>Reason * (min 10 chars)</Label>
                          <Textarea
                            value={createForm.reason}
                            onChange={(e) => setCreateForm({ ...createForm, reason: e.target.value })}
                            placeholder="Reason for this exception..."
                            required
                            minLength={10}
                            data-testid="create-reason-input"
                          />
                        </div>

                        <Button
                          type="submit"
                          className="w-full"
                          disabled={!createForm.exception_type || !createForm.client_id || !createForm.caregiver_id}
                          data-testid="create-submit-btn"
                        >
                          Create Exception
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Aging Report Details */}
        {showAgingReport && agingReport && (
          <div className="bg-white rounded-lg border border-slate-200 p-4 mb-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <BarChart3 className="h-5 w-5" /> Aging Report Details
            </h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-slate-600 mb-2">By Status</h4>
                {Object.entries(agingReport.by_status || {}).map(([status, count]) => (
                  <div key={status} className="flex justify-between py-1">
                    <Badge className={STATUS_COLORS[status]}>{status}</Badge>
                    <span className="font-medium">{count}</span>
                  </div>
                ))}
              </div>
              <div>
                <h4 className="text-sm font-medium text-slate-600 mb-2">By Type</h4>
                {Object.entries(agingReport.by_type || {}).map(([type, count]) => (
                  <div key={type} className="flex justify-between py-1">
                    <span>{TYPE_LABELS[type] || type}</span>
                    <span className="font-medium">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Exceptions Table */}
        <div className="bg-white rounded-lg border border-slate-200">
          <div className="p-4 border-b border-slate-200">
            <h3 className="font-semibold">
              Exceptions
              <span className="text-sm font-normal text-slate-500 ml-2">
                {exceptions.length} total
              </span>
            </h3>
          </div>

          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading...</div>
          ) : exceptions.length === 0 ? (
            <div className="p-8 text-center text-slate-500">No EVV exceptions found</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Caregiver</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Days Open</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {exceptions.map((exc) => {
                  const sourceInfo = SOURCE_LABELS[exc.source] || SOURCE_LABELS.Manual;
                  return (
                  <TableRow
                    key={exc.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => navigate(`/evv-exceptions/${exc.id}`)}
                    data-testid={`exception-row-${exc.id}`}
                  >
                    <TableCell>{exc.exception_date}</TableCell>
                    <TableCell>{TYPE_LABELS[exc.exception_type] || exc.exception_type}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Badge className={sourceInfo.color} variant="outline">
                          {sourceInfo.label}
                        </Badge>
                        {exc.needs_linking && (
                          <Badge variant="destructive" className="text-xs" data-testid={`needs-linking-badge-${exc.id}`}>
                            Needs Linking
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{exc.client_name || "-"}</TableCell>
                    <TableCell>{exc.caregiver_name || "-"}</TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLORS[exc.status]}>{exc.status}</Badge>
                    </TableCell>
                    <TableCell>
                      {exc.status !== "Closed" ? (
                        <span className={exc.days_open > 14 ? "text-red-600 font-medium" : ""}>
                          {exc.days_open} days
                        </span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/evv-exceptions/${exc.id}`);
                          }}
                          data-testid={`view-exception-${exc.id}`}
                        >
                          View
                        </Button>
                        {/* Link Visit button (Sprint 9) - only show for exceptions needing linking */}
                        {exc.needs_linking && canManage && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => openLinkModal(exc, e)}
                            data-testid={`link-visit-btn-${exc.id}`}
                          >
                            <Link2 className="h-3 w-3 mr-1" /> Link
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Link Visit Modal (Sprint 9) */}
        <Dialog open={showLinkModal} onOpenChange={setShowLinkModal}>
          <DialogContent data-testid="link-visit-modal">
            <DialogHeader>
              <DialogTitle>Link Visit to Exception</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {linkException && (
                <div className="bg-slate-50 rounded p-3 text-sm">
                  <div><strong>Exception:</strong> {linkException.exception_type} - {linkException.exception_date}</div>
                  <div><strong>Client:</strong> {linkException.client_name || "Unknown"}</div>
                  <div><strong>Caregiver:</strong> {linkException.caregiver_name || "Unknown"}</div>
                  {linkException.sandata_visit_id && (
                    <div><strong>Sandata Visit ID:</strong> {linkException.sandata_visit_id}</div>
                  )}
                </div>
              )}
              
              <div>
                <Label>Select Visit to Link</Label>
                {linkLoading ? (
                  <div className="text-sm text-slate-500 py-4">Loading visits...</div>
                ) : linkVisitsSearch.length === 0 ? (
                  <div className="text-sm text-slate-500 py-4">No visits found for this client.</div>
                ) : (
                  <Select value={linkVisitId} onValueChange={setLinkVisitId}>
                    <SelectTrigger data-testid="link-visit-select">
                      <SelectValue placeholder="Select a visit" />
                    </SelectTrigger>
                    <SelectContent>
                      {linkVisitsSearch.map(v => (
                        <SelectItem key={v.id} value={v.id}>
                          {formatDate(v.scheduled_start)} - {v.service_type} ({v.status})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowLinkModal(false)}>Cancel</Button>
              <Button 
                onClick={handleLinkVisit} 
                disabled={!linkVisitId || linkLoading}
                data-testid="link-visit-submit-btn"
              >
                <Link2 className="h-4 w-4 mr-2" /> Link Visit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default EVVInboxPage;
